// src/pages/Tabs/BalanceSheetPage.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { apiCall } from "@/api/apiCall";
import { AlertCircle, ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

/* ─── Shimmer Skeleton ─── */
const Skeleton = ({ className = "", style = {} }) => (
  <div className={`animate-pulse bg-[#c4c3c327] rounded ${className}`} style={style} />
);

/* ─── Arrows ──────────────────────────────────────── */
const Up   = () => <span style={{ color: "#70AD47", fontSize: 11 }}>▲</span>;
const Down = () => <span style={{ color: "#C90A0A", fontSize: 11 }}>▼</span>;

/* ─── Table cell styles ───────────────────────────── */
const border  = "1px solid var(--table-border-light)";
const borderB = "1px solid #d0d0d0";

const td = {
  base:    { padding: "7px 14px", borderBottom: border, verticalAlign: "middle", fontSize: 11.5, color: "var(--foreground)", background: "var(--card)" },
  grp:     { padding: "8px 14px", borderBottom: border, verticalAlign: "top",    fontSize: 11.5, color: "var(--foreground)", background: "var(--card)", paddingTop: 10 },
  type:    { padding: "7px 14px", borderBottom: border, verticalAlign: "top",    fontSize: 11.5, color: "var(--foreground)", background: "var(--card)", paddingTop: 9 },
  num:     { padding: "7px 14px", borderBottom: border, textAlign: "right",      fontSize: 11.5, color: "var(--foreground)", background: "var(--card)", verticalAlign: "middle" },
  pct:     { padding: "7px 14px", borderBottom: border, textAlign: "right",      fontSize: 11.5, color: "var(--foreground)", background: "var(--card)", verticalAlign: "middle", whiteSpace: "nowrap" },
  typeSep: { padding: "7px 14px", borderBottom: border, borderTop: borderB, verticalAlign: "top", fontSize: 11.5, color: "var(--foreground)", background: "var(--card)", paddingTop: 9 },
  numSep:  { padding: "7px 14px", borderBottom: border, borderTop: borderB, textAlign: "right", fontSize: 11.5, color: "var(--foreground)", background: "var(--card)", verticalAlign: "middle" },
  pctSep:  { padding: "7px 14px", borderBottom: border, borderTop: borderB, textAlign: "right", fontSize: 11.5, color: "var(--foreground)", background: "var(--card)", verticalAlign: "middle", whiteSpace: "nowrap" },
  tot:     { padding: "7px 14px", borderTop: "1.5px solid #aaa", borderBottom: "1.5px solid #aaa", fontWeight: 700, fontSize: 11.5, color: "var(--foreground)", background: "#edf5fb", verticalAlign: "middle" },
  totNum:  { padding: "7px 14px", borderTop: "1.5px solid #aaa", borderBottom: "1.5px solid #aaa", textAlign: "right", fontWeight: 700, fontSize: 11.5, color: "var(--foreground)", background: "#edf5fb", verticalAlign: "middle" },
  totPct:  { padding: "7px 14px", borderTop: "1.5px solid #aaa", borderBottom: "1.5px solid #aaa", textAlign: "right", fontWeight: 700, fontSize: 11.5, color: "var(--foreground)", background: "#edf5fb", verticalAlign: "middle", whiteSpace: "nowrap" },
};

/* ─── ✅ YELLOW HIGHLIGHT: sirf in 2 rows ke liye ── */
const YELLOW_TOT = ["Total Assets", "Total Equity & Liabilities"];
const totBg = (type) => YELLOW_TOT.includes(type) ? "#F7F7B0" : "#edf5fb";

const Spacer = () => (
  <tr>
    <td colSpan={6} style={{ height: 12, background: "var(--background)", border: "none" }} />
  </tr>
);

const isUp = (pct) => !pct?.startsWith("-");

const isNegative = (val) => {
  if (!val) return false;
  const s = String(val).trim();
  return s.startsWith("-") || (s.startsWith("(") && s.endsWith(")"));
};
const numColor = (val) => isNegative(val) ? "#C90A0A" : "var(--foreground)";

/* ─── Build all keys from nodes (recursive) ─────── */
const buildAllKeys = (nodes) => {
  const keys = [];
  const visit = (node) => {
    const key = node.type + "|" + node.depth;
    keys.push(key);
    node.children.forEach(visit);
  };
  nodes.forEach(visit);
  return keys;
};

/* ─── Helper: build tree from flat rows ──────────── */
const buildTree = (rows) => {
  const nonTot = rows.filter((r) => !r.is_total);
  const tot    = rows.find((r) => r.is_total);

  // Map banao: type -> node
  const nodeMap = {};
  nonTot.forEach((r) => {
    nodeMap[r.type] = { ...r, children: [] };
  });

  // Ab parent key se tree build karo
  const roots = [];
  nonTot.forEach((r) => {
    const node = nodeMap[r.type];
    if (r.parent === null) {
      roots.push(node);
    } else if (nodeMap[r.parent]) {
      nodeMap[r.parent].children.push(node);
    } else {
      // Parent nahi mila? Root mein daal do (fallback)
      roots.push(node);
    }
  });

  return { nodes: roots, tot };
};

/* ─── Parse sections from flat rows ─────────────────── */
const treeify = (rows) => buildTree(rows);

/* ─── NodeRow ─────────────────────────────────────── */
const NodeRow = ({ node, expandedMap, onToggle }) => {
  const hasChildren = node.children.length > 0;
  const isOpen      = !!expandedMap[node.type + "|" + node.depth];
  const indent      = (node.depth - 1) * 16;

  return (
    <>
      <tr>
        <td style={{ ...td.type, paddingLeft: indent + 12 }}>
          {hasChildren ? (
            <span
              onClick={() => onToggle(node.type + "|" + node.depth)}
              style={{ display: "inline-block", width: 14, marginRight: 4, fontSize: 9, color: "#4472C4", cursor: "pointer", userSelect: "none" }}
            >
              {isOpen ? "▼" : "▶"}
            </span>
          ) : (
            <span style={{ display: "inline-block", width: 18 }} />
          )}
          {node.type}
        </td>
        <td style={{ ...td.num, color: numColor(node.current_bal) }}>{node.current_bal ?? ""}</td>
        <td style={{ ...td.num, color: numColor(node.prior_bal)   }}>{node.prior_bal   ?? ""}</td>
        <td style={{ ...td.num, color: numColor(node.diff)        }}>{node.diff        ?? ""}</td>
        <td style={td.pct}>
          {node.pct_change}&nbsp;
          {node.pct_change !== "-" ? (isUp(node.pct_change) ? <Up /> : <Down />) : null}
        </td>
      </tr>
      {isOpen && node.children.map((child, idx) => (
        <NodeRow key={idx} node={child} expandedMap={expandedMap} onToggle={onToggle} />
      ))}
    </>
  );
};

/* ─── GroupRows ───────────────────────────────────── */
const GroupRows = ({ rows, expandedMap, onToggle }) => {
  if (!rows?.length) return null;

  const { nodes, tot } = treeify(rows);
  const groupLabel     = rows[0]?.group;

  if (nodes.length === 0 && tot) {
    return (
      <>
        <tr>
          <td style={td.grp}>{groupLabel}</td>
          {/* ✅ Yellow background for Total Assets / Total Liabilities & Equity */}
          <td style={{ ...td.tot, background: totBg(tot.type) }}>{tot.type}</td>
          <td style={{ ...td.totNum, background: totBg(tot.type), color: numColor(tot.current_bal) }}>{tot.current_bal ?? ""}</td>
          <td style={{ ...td.totNum, background: totBg(tot.type), color: numColor(tot.prior_bal)   }}>{tot.prior_bal   ?? ""}</td>
          <td style={{ ...td.totNum, background: totBg(tot.type), color: numColor(tot.diff)        }}>{tot.diff        ?? ""}</td>
          <td style={{ ...td.totPct, background: totBg(tot.type) }}>
            {tot.pct_change}&nbsp;
            {tot.pct_change !== "-" ? (isUp(tot.pct_change) ? <Up /> : <Down />) : null}
          </td>
        </tr>
        <Spacer />
      </>
    );
  }

  const countVisible = (node) => {
    const isOpen = !!expandedMap[node.type + "|" + node.depth];
    let count = 1;
    if (isOpen) node.children.forEach((ch) => { count += countVisible(ch); });
    return count;
  };

  const totalVisible = nodes.reduce((sum, n) => sum + countVisible(n), 0) + (tot ? 1 : 0);

  return (
    <>
      {nodes.map((node, idx) => (
        <React.Fragment key={idx}>
          <tr>
            {idx === 0 && (
              <td rowSpan={totalVisible} style={td.grp}>{groupLabel}</td>
            )}
            <td style={{ ...td.type, paddingLeft: 12, fontWeight: 700 }}>
              {node.children.length > 0 ? (
                <span
                  onClick={() => onToggle(node.type + "|" + node.depth)}
                  style={{ display: "inline-block", width: 14, marginRight: 4, fontSize: 9, color: "#4472C4", cursor: "pointer", userSelect: "none" }}
                >
                  {expandedMap[node.type + "|" + node.depth] ? "▼" : "▶"}
                </span>
              ) : (
                <span style={{ display: "inline-block", width: 18 }} />
              )}
              {node.type}
            </td>
            <td style={{ ...td.num, fontWeight: 700, color: numColor(node.current_bal) }}>{node.current_bal ?? ""}</td>
            <td style={{ ...td.num, fontWeight: 700, color: numColor(node.prior_bal)   }}>{node.prior_bal   ?? ""}</td>
            <td style={{ ...td.num, fontWeight: 700, color: numColor(node.diff)        }}>{node.diff        ?? ""}</td>
            <td style={{ ...td.pct, fontWeight: 700 }}>
              {node.pct_change}&nbsp;
              {node.pct_change !== "-" ? (isUp(node.pct_change) ? <Up /> : <Down />) : null}
            </td>
          </tr>

          {expandedMap[node.type + "|" + node.depth] &&
            node.children.map((child, cIdx) => (
              <NodeRow key={cIdx} node={child} expandedMap={expandedMap} onToggle={onToggle} />
            ))}
        </React.Fragment>
      ))}

      {/* ✅ Yellow background for Total Assets / Total Liabilities & Equity */}
      {tot && (
        <tr>
          <td style={{ ...td.tot, background: totBg(tot.type) }}>{tot.type}</td>
          <td style={{ ...td.totNum, background: totBg(tot.type), color: numColor(tot.current_bal) }}>{tot.current_bal ?? ""}</td>
          <td style={{ ...td.totNum, background: totBg(tot.type), color: numColor(tot.prior_bal)   }}>{tot.prior_bal   ?? ""}</td>
          <td style={{ ...td.totNum, background: totBg(tot.type), color: numColor(tot.diff)        }}>{tot.diff        ?? ""}</td>
          <td style={{ ...td.totPct, background: totBg(tot.type) }}>
            {tot.pct_change}&nbsp;
            {tot.pct_change !== "-" ? (isUp(tot.pct_change) ? <Up /> : <Down />) : null}
          </td>
        </tr>
      )}
      <Spacer />
    </>
  );
};

/* ─── Colors for sidebar distribution bars ────────── */
const DISTRO_COLORS = {
  Assets:      "#4472C4",
  Liabilities: "#C90A0A",
  Equity:      "#70AD47",
};

const parseNum = (str) => {
  if (!str) return 0;
  return parseFloat(str.replace(/,/g, "").replace(/[()]/g, (m) => (m === "(" ? "-" : ""))) || 0;
};

/* ─── Shimmer Sidebar ─────────────────────────────── */
const SidebarShimmer = () => (
  <div style={{ width: 210, minWidth: 210, background: "var(--card)", borderRight: "1px solid var(--border)", padding: "16px 14px", display: "flex", flexDirection: "column" }}>
    <Skeleton style={{ height: 14, width: "70%", marginBottom: 18 }} />
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} style={{ marginBottom: 10, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
        <Skeleton style={{ height: 10, width: "60%", marginBottom: 8 }} />
        <Skeleton style={{ height: 22, width: "80%" }} />
      </div>
    ))}
    <div style={{ background: "#edf5fb", borderRadius: 8, padding: "12px 10px", marginTop: 6 }}>
      <Skeleton style={{ height: 13, width: "60%", marginBottom: 14 }} />
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <Skeleton style={{ height: 11, width: "40%" }} />
            <Skeleton style={{ height: 11, width: "25%" }} />
          </div>
          <Skeleton style={{ height: 5, width: "100%", borderRadius: 3 }} />
        </div>
      ))}
    </div>
  </div>
);

/* ─── Shimmer Table ───────────────────────────────── */
const TableShimmer = () => {
  const thBase = {
    padding: "10px 14px",
    fontWeight: 600,
    fontSize: 12,
    color: "var(--foreground)",
    borderTop: "none", borderLeft: "none", borderRight: "none",
    borderBottom: "2px solid var(--foreground)",
    background: "var(--card)",
    textAlign: "left",
    lineHeight: 1.3,
    verticalAlign: "bottom",
  };

  return (
    <div style={{ flex: 1, padding: "16px 24px", overflow: "auto", background: "var(--background)" }}>
      <Skeleton style={{ height: 30, borderRadius: 6, marginBottom: 16, marginTop: -8 }} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 0, paddingBottom: 10 }}>
        <Skeleton style={{ height: 18, width: 130 }} />
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", background: "var(--card)" }}>
        <thead>
          <tr>
            {["Group", "Type", "Current Period Bal", "Prior Period Bal", "Current vs Prior Diff", "% Change"].map((col, i) => (
              <th key={i} style={{ ...thBase, textAlign: i >= 2 ? "right" : "left" }}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 3 }).map((_, groupIdx) => (
            <React.Fragment key={groupIdx}>
              {Array.from({ length: 4 }).map((_, rowIdx) => (
                <tr key={rowIdx}>
                  {rowIdx === 0 && (
                    <td rowSpan={4} style={{ padding: "8px 14px", verticalAlign: "top", background: "var(--card)" }}>
                      <Skeleton style={{ height: 13, width: 70 }} />
                    </td>
                  )}
                  <td style={{ padding: "7px 14px", borderBottom: "1px solid var(--table-border-light)", background: rowIdx === 3 ? "#edf5fb" : "var(--card)" }}>
                    <Skeleton style={{ height: 12, width: rowIdx === 3 ? "80%" : "65%" }} />
                  </td>
                  {[1, 2, 3, 4].map((col) => (
                    <td key={col} style={{ padding: "7px 14px", borderBottom: "1px solid var(--table-border-light)", textAlign: "right", background: rowIdx === 3 ? "#edf5fb" : "var(--card)" }}>
                      <Skeleton style={{ height: 12, width: "70%", marginLeft: "auto" }} />
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <td colSpan={6} style={{ height: 12, background: "var(--background)", border: "none", borderBottom: "1px solid var(--table-border-light)" }} />
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ══════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════ */
const BalanceSheetPage = ({
  selectedContract,
  selectedContractLabel,
  selectedDisplay,
  selectedCurrency,
  selectedYear,
  selectedMonth,
}) => {

  const [hasApplied,   setHasApplied]   = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [cards,        setCards]        = useState([]);
  const [summary,      setSummary]      = useState([]);
  const [balanceTable, setBalanceTable] = useState([]);

  const [expandedMap,  setExpandedMap]  = useState({});
  const [expandLevel,  setExpandLevel]  = useState(0);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        contract: selectedContract,
        display:  selectedDisplay,
        currency: selectedCurrency,
        year:     selectedYear,
        month:    selectedMonth,
      };
      const res = await apiCall("get", "/get-balance-sheet", params, null, false, false);
      if (res?.success) {
        setCards(res.data?.cards                || []);
        setSummary(res.data?.summary            || []);
        setBalanceTable(res.data?.balance_table || []);
        setExpandedMap({});
        setExpandLevel(0);
      }
    } catch (err) {
      console.error("Balance sheet fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedContract && selectedDisplay && selectedCurrency && selectedYear && selectedMonth) {
      setHasApplied(true);
      fetchData();
    } else {
      setHasApplied(false);
      setCards([]);
      setSummary([]);
      setBalanceTable([]);
      setExpandedMap({});
      setExpandLevel(0);
    }
  }, [selectedContract, selectedDisplay, selectedCurrency, selectedYear, selectedMonth]);

  const { treeNodes, allKeys, maxDepth } = useMemo(() => {
    const nonTot = balanceTable.filter((r) => !r.is_total);
    const nodes  = [];
    const stack  = [];

    nonTot.forEach((r) => {
      const node = { ...r, children: [] };
      while (stack.length && stack[stack.length - 1].depth >= r.depth) stack.pop();
      if (stack.length === 0) nodes.push(node);
      else stack[stack.length - 1].children.push(node);
      stack.push(node);
    });

    const keys = [];
    let max = 0;
    const visit = (node) => {
      const key = node.type + "|" + node.depth;
      keys.push(key);
      if (node.depth > max) max = node.depth;
      node.children.forEach(visit);
    };
    nodes.forEach(visit);

    return { treeNodes: nodes, allKeys: keys, maxDepth: max };
  }, [balanceTable]);

  const [showLevelDropdown, setShowLevelDropdown] = useState(false);

  const handleExpandToLevel = useCallback((targetLevel) => {
    if (targetLevel < 1 || targetLevel > maxDepth) return;
    const map = {};
    allKeys.forEach((k) => {
      const depth = parseInt(k.split("|")[1], 10);
      if (depth <= targetLevel) map[k] = true;
    });
    setExpandedMap(map);
    setExpandLevel(targetLevel);
    setShowLevelDropdown(false);
  }, [maxDepth, allKeys]);

  const handleCollapseAll = useCallback(() => {
    setExpandedMap({});
    setExpandLevel(0);
    setShowLevelDropdown(false);
  }, []);

  const handleToggle = useCallback((key) => {
    setExpandedMap((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const groupedRows = balanceTable.reduce((acc, row) => {
    if (!acc[row.group]) acc[row.group] = [];
    acc[row.group].push(row);
    return acc;
  }, {});

  const maxSummaryVal = Math.max(...summary.map((s) => Math.abs(parseNum(s.value))), 1);

  const thBase = {
    padding: "10px 14px",
    fontWeight: 600,
    fontSize: 12,
    color: "var(--foreground)",
    borderTop: "none", borderLeft: "none", borderRight: "none",
    borderBottom: "2px solid var(--foreground)",
    background: "var(--card)",
    textAlign: "left",
    lineHeight: 1.3,
    verticalAlign: "bottom",
  };

  const contractDescription = selectedContractLabel ?? selectedContract;
  const isFullyExpanded = expandLevel >= maxDepth;

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100%", background: "var(--background)", color: "var(--foreground)", fontFamily: "'Segoe UI', Arial, sans-serif" }}>

      {!hasApplied ? (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
          <Card className="w-full max-w-md text-center">
            <CardHeader>
              <CardTitle className="text-destructive flex items-center gap-2 justify-center">
                <AlertCircle className="w-5 h-5" />
                <span>Select filters to view Balance Sheet.</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Please select Contract, Display and Currency to view the Balance Sheet
              </p>
            </CardContent>
          </Card>
        </div>

      ) : loading ? (
        <div style={{ display: "flex", flex: 1 }}>
          <SidebarShimmer />
          <TableShimmer />
        </div>

      ) : (

        <div style={{ display: "flex", flex: 1 }}>

          {/* ════ SIDEBAR ════ */}
          <div style={{ width: 310, minWidth: 210, background: "var(--card)", borderRight: "1px solid var(--border)", padding: "16px 14px", display: "flex", flexDirection: "column" }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: "var(--foreground)", marginBottom: 14 }}>BALANCE SHEET KPIs</div>

            {cards.map((k, i) => (
              <div key={i} style={{ marginBottom: 10, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: "10px 12px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
                <div style={{ fontSize: 9, fontWeight: 500, letterSpacing: 0.5, color: "var(--muted-foreground)", textTransform: "uppercase", marginBottom: 4 }}>
                  {k.label}
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div style={{ fontSize: 20, fontWeight: 500, color: numColor(k.value) || "var(--navy-dark)", lineHeight: 1.1 }}>
                    {k.value}
                  </div>
                  {k.pct_change && (
                    <div style={{ fontSize: 11, fontWeight: 600, color: k.pct_change?.startsWith("-") ? "#C90A0A" : "#70AD47", display: "flex", alignItems: "center", gap: 2 }}>
                      {k.pct_change?.startsWith("-") ? "▼" : "▲"} {k.pct_change}
                    </div>
                  )}
                </div>
              </div>
            ))}

            <div style={{ background: "#edf5fb", borderRadius: 8, padding: "12px 10px", marginTop: 6 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: "var(--navy-dark)", marginBottom: 10 }}>Group Distribution</div>
              {summary.map((d, i) => {
                const barWidth = `${Math.min((Math.abs(parseNum(d.value)) / maxSummaryVal) * 100, 100).toFixed(0)}%`;
                const color    = DISTRO_COLORS[d.label] || "#4472C4";
                return (
                  <div key={i} style={{ marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, marginBottom: 3 }}>
                      <span>{d.label}</span>
                      <span style={{ fontWeight: 600 }}>{d.value}</span>
                    </div>
                    <div style={{ height: 5, background: "var(--muted)", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{ height: 5, width: barWidth, background: color, borderRadius: 3 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ════ MAIN TABLE ════ */}
          <div style={{ flex: 1, padding: "16px 24px", overflow: "auto", background: "var(--background)" }}>
            <div style={{ textAlign: "center", fontSize: 12, fontWeight: 600, letterSpacing: 0.8, color: "var(--foreground)", marginBottom: 16, marginTop: -8, backgroundColor: "#DDEBF7", padding: "6px 0", borderRadius: 6 }}>
              {contractDescription
                ? `${contractDescription} - FINANCIAL BALANCE SHEET`
                : "FINANCIAL BALANCE SHEET"}
            </div>

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 0, flexWrap: "wrap", gap: 8, paddingBottom: 10 }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: "var(--foreground)" }}>BALANCE TABLE</span>

              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>

                <div style={{ position: "relative" }}>
                  <button
                    onClick={() => setShowLevelDropdown((v) => !v)}
                    title="Select level to expand"
                    style={{
                      display: "flex", alignItems: "center", gap: 4,
                      padding: "4px 10px", borderRadius: 5,
                      border: "1px solid #4472C4",
                      background: showLevelDropdown ? "#EBF1FF" : "#fff",
                      color: "#4472C4",
                      fontSize: 11, fontWeight: 600,
                      cursor: "pointer",
                      transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#EBF1FF"; }}
                    onMouseLeave={(e) => { if (!showLevelDropdown) e.currentTarget.style.background = "#fff"; }}
                  >
                    <ChevronsUpDown size={13} />
                    {expandLevel === 0 ? "Expand" : `Level ${expandLevel}`}
                    <span style={{ fontSize: 8, marginLeft: 2 }}>▼</span>
                  </button>

                  {showLevelDropdown && (
                    <>
                      <div
                        style={{ position: "fixed", inset: 0, zIndex: 999 }}
                        onClick={() => setShowLevelDropdown(false)}
                      />
                      <div style={{
                        position: "absolute", top: "calc(100% + 6px)", right: 0,
                        zIndex: 1000,
                        background: "#fff",
                        border: "1px solid #d0d8e8",
                        borderRadius: 8,
                        boxShadow: "0 6px 24px rgba(68,114,196,0.13)",
                        minWidth: 160,
                        padding: "6px 0",
                        overflow: "hidden",
                      }}>
                        <div style={{
                          padding: "6px 14px 8px",
                          fontSize: 10, fontWeight: 700,
                          color: "#888", letterSpacing: 0.6,
                          textTransform: "uppercase",
                          borderBottom: "1px solid #eee",
                          marginBottom: 4,
                        }}>
                          Select Level
                        </div>

                        {Array.from({ length: maxDepth }, (_, i) => i + 1).map((lvl) => {
                          const isActive = expandLevel === lvl;
                          return (
                            <div
                              key={lvl}
                              onClick={() => handleExpandToLevel(lvl)}
                              style={{
                                display: "flex", alignItems: "center", gap: 8,
                                padding: "7px 14px",
                                fontSize: 11, fontWeight: isActive ? 700 : 500,
                                color: isActive ? "#4472C4" : "#333",
                                background: isActive ? "#EBF1FF" : "transparent",
                                cursor: "pointer",
                                transition: "background 0.12s",
                              }}
                              onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "#f5f8ff"; }}
                              onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}
                            >
                              <div style={{
                                width: lvl * 5,
                                height: 3,
                                borderRadius: 2,
                                background: isActive ? "#4472C4" : "#bbb",
                                transition: "width 0.15s",
                              }} />
                              Level {lvl}
                              {isActive && (
                                <span style={{ marginLeft: "auto", fontSize: 10, color: "#4472C4" }}>✓</span>
                              )}
                            </div>
                          );
                        })}

                        <div style={{ borderTop: "1px solid #eee", margin: "4px 0" }} />

                        <div
                          onClick={() => handleExpandToLevel(maxDepth)}
                          style={{
                            display: "flex", alignItems: "center", gap: 8,
                            padding: "7px 14px",
                            fontSize: 11, fontWeight: 600,
                            color: isFullyExpanded ? "#aaa" : "#4472C4",
                            cursor: isFullyExpanded ? "default" : "pointer",
                            transition: "background 0.12s",
                          }}
                          onMouseEnter={(e) => { if (!isFullyExpanded) e.currentTarget.style.background = "#f5f8ff"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                        >
                          <ChevronsUpDown size={12} />
                          Expand All
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <button
                  onClick={handleCollapseAll}
                  disabled={expandLevel === 0}
                  title="Collapse All"
                  style={{
                    display: "flex", alignItems: "center", gap: 4,
                    padding: "4px 10px", borderRadius: 5,
                    border: `1px solid ${expandLevel === 0 ? "#ddd" : "#aaa"}`,
                    background: "#fff",
                    color: expandLevel === 0 ? "#ccc" : "#555",
                    fontSize: 11, fontWeight: 600,
                    cursor: expandLevel === 0 ? "not-allowed" : "pointer",
                    transition: "background 0.15s",
                    opacity: expandLevel === 0 ? 0.6 : 1,
                  }}
                  onMouseEnter={(e) => { if (expandLevel !== 0) e.currentTarget.style.background = "#f5f5f5"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}
                >
                  <ChevronsDownUp size={13} />
                  Collapse All
                </button>
              </div>
            </div>

            <table style={{ width: "100%", borderCollapse: "collapse", background: "var(--card)" }}>
              <thead>
                <tr>
                  <th style={{ ...thBase, width: 80  }}>Group</th>
                  <th style={{ ...thBase, width: 140 }}>Type</th>
                  <th style={{ ...thBase, textAlign: "right", width: 120 }}>Current<br/>Period Bal</th>
                  <th style={{ ...thBase, textAlign: "right", width: 110 }}>Prior Period<br/>Bal</th>
                  <th style={{ ...thBase, textAlign: "right", width: 120 }}>Current vs<br/>Prior Diff</th>
                  <th style={{ ...thBase, textAlign: "right", width: 90  }}>% Change</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(groupedRows).map((group) => (
                  <GroupRows
                    key={group}
                    rows={groupedRows[group]}
                    expandedMap={expandedMap}
                    onToggle={handleToggle}
                  />
                ))}
              </tbody>
            </table>
          </div>

        </div>
      )}

    </div>
  );
};

export default BalanceSheetPage;